import { ref } from 'vue'
import { defineStore } from 'pinia'
import api from '@/utils/api'
import { API_ROUTES } from '@/const/apiRoutes'

export interface GraduationStatus {
  outtalent_graduation_status: string | null
  has_interview_preparation_plan: boolean
  legal_name: string | null
  slack_member_id: string | null
  core_referral_text: string | null
  core_referral_text_status: 'none' | 'submitted' | 'approved' | null
  resume_status: string | null
  behavioral_status: string | null
  coding_status: string | null
  system_design_status: string | null
}

export interface GraduatingFellow {
  user: {
    id: number
    email: string
    username: string
    slack_member_id: string | null
    first_name: string
    last_name: string
    avatar_image: string | null
    linkedin_profile_url: string | null
  }
  legal_name: string
  resume_status: string | null
  resume_status_set_date: string | null
  behavioral_status: string | null
  behavioral_status_set_date: string | null
  coding_status: string | null
  coding_status_set_date: string | null
  system_design_status: string | null
  system_design_status_set_date: string | null
  default_resume_url: string | null
  graduation_started_at: string | null
  program_started_at: string | null
  campus_earned_points: number
  core_referral_text: string | null
  core_referral_text_status: 'none' | 'submitted' | 'approved' | null
  can_edit_referral_text: boolean
  testimonials: Array<{
    author_id: number
    author_name: string
    created_at: string
    role: string
    content: string
  }>
  testimonials_summary: string | null
  graduation_flow: GraduationFlow | null
  max_referral_tier: CompanyTier | null
  current_plan_name: string | null
  package_name: string | null
  job_support_group_key: string | null
}

export type GraduationFlow = 'legacy' | 'interview_prep'
export type CompanyTier = 'STANDARD' | 'NOTABLE' | 'TOP_TIER'

export interface CompletedFellow {
  user: GraduatingFellow['user']
  graduation_status: string
  coding_status: string | null
  coding_status_set_date: string | null
  program_completed_at: string | null
  program_completed_notified_at: string | null
  pending_pod_placement: boolean
  job_support_group_key: string
  current_plan: {
    public_id: string
    name: string
    graduation_flow: GraduationFlow
    program_completion: 'coding_exam' | 'manual'
  } | null
  allowed_next_plans: Array<{ public_id: string; name: string; amount: string; interval: string }>
  can_offer_package: boolean
  can_mark_completed: boolean
  latest_pod: { id: number; name: string } | null
  assigned_plan: { public_id: string; name: string } | null
  assigned_product: { public_id: string; name: string } | null
  latest_purchase: {
    id: number
    product_name: string | null
    starts_at: string | null
    expires_at: string | null
  } | null
}

export type CompletedStage = 'next_step' | 'payment' | 'placement'

export function completedFellowStage(fellow: CompletedFellow): CompletedStage {
  if (fellow.pending_pod_placement) return 'placement'
  // After a purchase the webhook points assigned_plan at the bought plan, so only a
  // plan different from the current one is still waiting for payment.
  const planLinkSent =
    !!fellow.assigned_plan && fellow.assigned_plan.public_id !== fellow.current_plan?.public_id
  return planLinkSent || fellow.assigned_product ? 'payment' : 'next_step'
}

export const useGraduationStore = defineStore('graduation', () => {
  const fellows = ref<GraduatingFellow[]>([])
  const completedFellows = ref<CompletedFellow[]>([])
  const jobSupportGroups = ref<Array<{ handle: string; name: string }>>([])
  const status = ref<GraduationStatus | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  async function fetchGraduationStatus() {
    const response = await api.get(API_ROUTES.GRADUATION_STATUS)
    status.value = response.data
    return response.data as GraduationStatus
  }

  async function submitGraduation(payload: {
    slack_member_id: string
    legal_name: string
    core_referral_text: string
  }) {
    const response = await api.post(API_ROUTES.GRADUATION_SUBMIT, payload)
    return response.data
  }

  async function fetchGraduatingFellows() {
    isLoading.value = true
    error.value = null
    try {
      const response = await api.get(API_ROUTES.GRADUATING_FELLOWS_LIST)
      fellows.value = response.data
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
      error.value = detail || 'Failed to load graduating fellows.'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function updateReferralPackage(
    userId: number,
    payload: { core_referral_text?: string; core_referral_text_status?: string },
  ) {
    const response = await api.patch(API_ROUTES.GRADUATION_REFERRAL_PACKAGE(userId), payload)
    // Sync local state
    const fellow = fellows.value.find((f) => f.user.id === userId)
    if (fellow) {
      if (payload.core_referral_text !== undefined) {
        fellow.core_referral_text = payload.core_referral_text
      }
      if (payload.core_referral_text_status !== undefined) {
        fellow.core_referral_text_status =
          payload.core_referral_text_status as GraduatingFellow['core_referral_text_status']
      }
    }
    return response.data
  }

  async function graduateFellow(
    userId: number,
    jobApplicationMentorUserId: number | null,
    jobSupportGroupKey: string | null = null,
    postProgram: {
      flow?: 'package' | 'job_support' | 'none'
      offerProductId?: string | null
      extendedAccessMonths?: number | null
      maxReferralTier?: CompanyTier | null
    } = {},
  ) {
    const response = await api.post(API_ROUTES.GRADUATION_GRADUATE(userId), {
      job_application_mentor_user_id: jobApplicationMentorUserId ?? null,
      job_support_group_key: jobSupportGroupKey ?? null,
      post_program_flow: postProgram.flow ?? 'none',
      offer_product_id: postProgram.offerProductId ?? null,
      extended_access_months: postProgram.extendedAccessMonths ?? null,
      max_referral_tier: postProgram.maxReferralTier ?? null,
    })
    // Remove graduated fellow from local list
    fellows.value = fellows.value.filter((f) => f.user.id !== userId)
    return response.data
  }

  async function fetchCompletedFellows() {
    isLoading.value = true
    error.value = null
    try {
      const response = await api.get(API_ROUTES.COMPLETED_FELLOWS_LIST)
      completedFellows.value = response.data
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
      error.value = detail || 'Failed to load fellows who completed the program.'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function fetchProgramFlow(userId: number) {
    const response = await api.get(API_ROUTES.GRADUATION_PROGRAM_FLOW(userId))
    return response.data as CompletedFellow
  }

  async function markProgramCompleted(userId: number) {
    const response = await api.post(API_ROUTES.GRADUATION_COMPLETE_PROGRAM(userId))
    const fellow = completedFellows.value.find((f) => f.user.id === userId)
    if (fellow) {
      Object.assign(fellow, response.data, { program_completed_notified_at: null, can_mark_completed: false })
    }
    return response.data as { graduation_status: string; program_completed_at: string }
  }

  async function notifyProgramCompleted(userId: number) {
    const response = await api.post(API_ROUTES.GRADUATION_NOTIFY_PROGRAM_COMPLETED(userId))
    const fellow = completedFellows.value.find((f) => f.user.id === userId)
    if (fellow) {
      fellow.program_completed_notified_at = response.data.program_completed_notified_at
    }
    return response.data as { program_completed_notified_at: string }
  }

  async function offerPlan(userId: number, planPublicId: string) {
    const response = await api.post(API_ROUTES.SUBSCRIPTION_ASSIGN_PLAN(userId), {
      plan_id: planPublicId,
      send_email: true,
    })
    const fellow = completedFellows.value.find((f) => f.user.id === userId)
    if (fellow) {
      const plan = response.data.assigned_plan
      fellow.assigned_plan = plan ? { public_id: plan.public_id, name: plan.name } : null
    }
    return response.data
  }

  async function offerProduct(userId: number, productPublicId: string, jobSupportGroupKey: string) {
    const response = await api.post(API_ROUTES.SUBSCRIPTION_ASSIGN_PRODUCT(userId), {
      product_id: productPublicId,
      job_support_group_key: jobSupportGroupKey,
      send_email: true,
    })
    const fellow = completedFellows.value.find((f) => f.user.id === userId)
    const p = response.data.assigned_product
    if (fellow) fellow.assigned_product = p ? { public_id: p.public_id, name: p.name } : null
    if (fellow) fellow.job_support_group_key = jobSupportGroupKey
    return response.data
  }

  async function fetchJobSupportGroups() {
    if (jobSupportGroups.value.length) return
    jobSupportGroups.value = (await api.get(API_ROUTES.JOB_SUPPORT_GROUP_OPTIONS)).data
  }

  return {
    fellows,
    completedFellows,
    jobSupportGroups,
    fetchJobSupportGroups,
    status,
    isLoading,
    error,
    fetchGraduationStatus,
    submitGraduation,
    fetchGraduatingFellows,
    updateReferralPackage,
    graduateFellow,
    fetchCompletedFellows,
    fetchProgramFlow,
    markProgramCompleted,
    notifyProgramCompleted,
    offerPlan,
    offerProduct,
  }
})
